import React, { ReactElement, useEffect } from 'react';
import styled from '@emotion/styled';
import ReactModal from 'react-modal';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setCookie, isDarkMode, convertMinutesToMs, getCookie } from '../../utils/helpers';
import breakpoints from '../../theme/material/breakpoints';
import { toaster } from '../../common';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { updateCookieConsent } from '../../utils/dataLayer';
import { FONT_FAMILIES } from '../../theme/material/constants';
import colors from '../../theme/colors';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { RootState } from '../../store';
import PrimaryButtonV2 from 'atoms/buttons/PrimaryButtonV2';
import SecondaryButton from 'atoms/buttons/SecondaryButton';
import { trackConsentDecision } from '../../utils/vysion/privacy';
import useCookieConsentCheck from '../../hooks/useCookieConsentCheck';
import { updateCookieConsentValue } from '../../actions/settingActions';

ReactModal.setAppElement('body');

interface CookieConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Palette = {
  bg: string;
  heading: string;
  body: string;
  link: string;
  border: string;
  shadow: string;
};

const getPalette = (dark: boolean): Palette => ({
  bg: dark ? '#041A2A' : '#FFFFFF',
  heading: dark ? colors.dark.black.primary : '#081726',
  body: dark ? colors.dark.black.secondary : '#525d67',
  link: dark ? colors.dark.blue.secondary : colors.blue[500],
  border: dark ? colors.dark.blue.secondary : colors.blue[300],
  shadow: dark ? '0 -10px 16px rgba(0, 0, 0, 0.35)' : '0 -10px 16px rgba(0, 0, 0, 0.15)',
});

const setModalStyle = (palette: Palette) => {
  return {
    overlay: {
      zIndex: 10000,
      backgroundColor: 'rgba(0, 0, 0, 0)',
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'flex-end' as const,
      justifyContent: 'center' as const,
      padding: 0,
      pointerEvents: 'none' as const,
    },
    content: {
      position: 'relative' as const,
      bottom: 0,
      left: 'auto',
      right: 'auto',
      top: 'auto',
      transform: 'none',
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      padding: '24px 32px 40px',
      backgroundColor: palette.bg,
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      border: 'none',
      outline: 'none',
      boxShadow: palette.shadow,
      pointerEvents: 'auto' as const,
      [breakpoints.down('sm')]: {
        padding: '20px 20px 30px',
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        boxShadow: '0 -5px 25px rgba(0, 0, 0, 0.1)',
      },
    },
  };
};

const ModalContentWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  width: '100%',
  [breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
});

const ContentSection = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
});

const Title = styled('h2')<{ palette: Palette }>(({ palette }) => ({
  fontFamily: FONT_FAMILIES.secondary,
  fontSize: '20px',
  fontWeight: 400,
  marginBottom: 0,
  marginTop: 0,
  color: palette.heading,
  lineHeight: '18px',
  [breakpoints.down('sm')]: {
    fontSize: '20px',
    lineHeight: '20px',
    marginBottom: '8px',
    color: palette.heading,
  },
}));

const Description = styled('div')<{ palette: Palette }>(({ palette }) => ({
  fontFamily: FONT_FAMILIES.primary,
  fontSize: '15px',
  fontWeight: 400,
  color: palette.body,
  lineHeight: '18px',
  '& p': {
    marginBottom: '10px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  '& .link-line': {
    fontSize: '10px',
    lineHeight: '18px',
    marginTop: '10px',
  },
  '& strong': {
    fontWeight: 700,
  },
  [breakpoints.down('sm')]: {
    fontSize: '13px',
    lineHeight: '18px',
    '& p': {
      marginBottom: '10px',
    },
    '& .link-line': {
      fontSize: '10px',
      lineHeight: '18px',
      marginTop: '6px',
    },
  },
}));

const PrivacyLink = styled('a')<{ palette: Palette }>(({ palette }) => ({
  color: palette.link,
  textDecoration: 'underline',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '17px',
  '&:hover': {
    textDecoration: 'underline',
  },
  [breakpoints.down('sm')]: {
    fontSize: '10px',
    lineHeight: '18px',
  },
}));

const ButtonsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: '12px',
  minWidth: '200px',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexShrink: 0,
  [breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'row',
    gap: '16px',
  },
});

/**
 * Derive a readable page name from the current pathname
 * Covers all routes from src/router/private.tsx
 */
const getPageNameFromPath = (pathname: string, accountHashId?: string): string => {
  // Handle root path
  if (pathname === '/' || pathname === '') {
    return 'Dashboard';
  }

  const pathParts = pathname.split('/').filter(Boolean);
  const firstSegment = pathParts[0] || '';
  const secondSegment = pathParts[1] || '';

  // Handle nested routes with specific second segments
  if (firstSegment === 'billing') {
    const billingPages: { [key: string]: string } = {
      success: 'Billing Success',
      order: 'Billing Order',
      cancelled: 'Billing Cancelled',
      history: 'Billing History',
    };

    return billingPages[secondSegment] || 'Billing';
  }

  if (firstSegment === 'mail') {
    const mailPages: { [key: string]: string } = {
      unread: 'Messages Unread',
      locked: 'Messages Locked',
      unlocked: 'Messages Unlocked',
      archive: 'Messages Archive',
    };

    // If second segment is a hashId (not a known mail page), it's a conversation
    if (secondSegment && !mailPages[secondSegment]) {
      return 'Messages Conversation';
    }

    return mailPages[secondSegment] || 'Messages';
  }

  if (firstSegment === 'interests') {
    const interestsPages: { [key: string]: string } = {
      'favorited-me': 'Favorited Me',
      'my-favorites': 'My Favorites',
    };

    return interestsPages[secondSegment] || 'Interests';
  }

  if (firstSegment === 'settings') {
    if (secondSegment === 'email-password') {
      return 'Settings Email Password';
    }
    return 'Settings';
  }

  // Check if it's a profile page and determine if it's own profile
  if (firstSegment === 'profile') {
    if (pathParts.length >= 2) {
      const hashIdFromPath = pathParts[1];

      const isOwnProfile = accountHashId && hashIdFromPath === accountHashId;

      return isOwnProfile ? 'Edit My Profile' : 'View Profile';
    }
    return 'Profile Page';
  }

  // Map all other routes to readable page names
  const pageNameMap: { [key: string]: string } = {
    dashboard: 'Dashboard',
    search: 'Search',
    photos: 'Photos',
    offers: 'Offers',
    packages: 'Packages',
    'idv-packages': 'IDV Packages',
    payment: 'Payment',
    credits: 'Credits',
    deactivate: 'Deactivate',
    'gdpr-delete': 'GDPR Delete',
    dismissed: 'Dismissed',
    blocked: 'Blocked',
    biometrics: 'Biometrics',
    'privacy-updated': 'Privacy Updated',
    demo: 'Demo',
    verification: 'Verification',
    'cancelled-offers': 'Cancelled Offers',
  };

  // Return mapped name or capitalize the first segment
  return pageNameMap[firstSegment] || firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1) + ' Page';
};

/**
 * Determine trigger reason for cookie consent modal
 *
 * Note: Testing Limitation
 * - "Denied on Public Site" and "Accepted on Public Site" trigger reasons are determined
 *   based on existing cookie values, but the modal won't naturally show when the cookie
 *   is already set (per `useCookieConsentCheck` hook logic which prevents modal display
 *   when cookie is 'accepted' or 'rejected').
 * - To test these scenarios, the modal would need to be forced to display (e.g., via
 *   test mode flag or policy change detection).
 * - "Forced by App Requirement" trigger reason also won't show the modal since GPC
 *   active prevents modal display.
 */
const getCookieConsentTriggerReason = (
  isGpcActive: boolean
): 'No Preference' | 'Denied on Public Site' | 'Accepted on Public Site' | 'Forced by App Requirement' => {
  if (isGpcActive) {
    return 'Forced by App Requirement';
  }
  const cookieValue = getCookie('cookie_consent');

  if (cookieValue === 'rejected') {
    return 'Denied on Public Site';
  }

  if (cookieValue === 'accepted') {
    return 'Accepted on Public Site';
  }

  return 'No Preference';
};

/**
 * Cookie Consent Modal Component
 *
 * IMPORTANT: Core functionality cookies are exempt from consent restrictions
 * and will continue to work regardless of user's consent choice. These include:
 * - Selfie verification cookies (required for user verification)
 * - VWO cookies (required for A/B testing and site optimization)
 * - MixPanel cookies (required for analytics and user behavior tracking)
 * - Vysion cookies (required for fraud prevention and security)
 *
 * These cookies are not affected by gtag consent settings and will always be set
 * to ensure the site functions properly.
 */
const CookieConsentModal = ({ isOpen, onClose }: CookieConsentModalProps): ReactElement => {
  const dispatch = useDispatch();
  const { isMobile } = useMediaBreakpoints();
  const location = useLocation();
  const account = useSelector((state: RootState) => state.profile);
  const { isGpcActive } = useCookieConsentCheck();

  // Get cookie consent settings from Redux
  const settings = useSelector((state: RootState) => state.settings);
  const cookieConsentConfig = settings?.cookie_consent;

  // Derive page context and path from current location
  const pageContext = getPageNameFromPath(location.pathname, account?.hash_id);
  const path = location.pathname;
  const triggerReason = getCookieConsentTriggerReason(isGpcActive);

  // Track when modal is shown
  useEffect(() => {
    if (isOpen) {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.COOKIE_CONSENT_MODAL,
        payload: {
          eventProperties: {
            'Trigger Reason': triggerReason,
          },
        },
      });
    }
  }, [isOpen, triggerReason]);

  const handleAllowAll = (): void => {
    // Get expiry time from settings or default to 1 year (525600 minutes)
    const expiryMinutes = cookieConsentConfig?.actionExpiryMins || 525600;
    const expiryMs = convertMinutesToMs(expiryMinutes);

    // Set cookie with configured expiration
    setCookie('cookie_consent', 'accepted', false, expiryMs);
    // Update Redux state
    dispatch(updateCookieConsentValue('accepted'));
    // Update gtag consent to granted
    updateCookieConsent('granted');
    // Track user decision
    mixpanelTrackEvent({
      eventName: EVENT_NAMES.COOKIE_CONSENT_MODAL,
      payload: {
        eventProperties: {
          'Consent Decision': 'Allow all',
        },
        userProperties: {
          'Cookie Consent': 'Allow all',
        },
      },
    });
    // Track Vysion consent decision event
    trackConsentDecision('Allow all', pageContext, path);
    // Trigger cross-tab sync
    localStorage.setItem('cookie_consent_changed', Date.now().toString());
    // Close modal
    onClose();
  };

  const handleDenyAll = (): void => {
    // Get expiry time from settings or default to 1 year (525600 minutes)
    const expiryMinutes = cookieConsentConfig?.actionExpiryMins || 525600;
    const expiryMs = convertMinutesToMs(expiryMinutes);

    // Set cookie with configured expiration
    setCookie('cookie_consent', 'rejected', false, expiryMs);
    // Update Redux state
    dispatch(updateCookieConsentValue('rejected'));
    // Update gtag consent to denied
    updateCookieConsent('denied');
    // Track user decision
    mixpanelTrackEvent({
      eventName: EVENT_NAMES.COOKIE_CONSENT_MODAL,
      payload: {
        eventProperties: {
          'Consent Decision': 'Deny all',
        },
        userProperties: {
          'Cookie Consent': 'Deny all',
        },
      },
    });
    // Track Vysion consent decision event
    trackConsentDecision('Deny all', pageContext, path);
    // Show toast notification
    toaster.success('Successfully opted-out of third-party cookies');
    // Trigger cross-tab sync
    localStorage.setItem('cookie_consent_changed', Date.now().toString());
    // Close modal
    onClose();
  };

  const palette = getPalette(isDarkMode());

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <ReactModal
      isOpen={isOpen}
      style={setModalStyle(palette)}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      onRequestClose={() => {
        // Track when modal is closed without making a decision
        trackConsentDecision('Closed', pageContext, path);
        // Prevent closing without selection
      }}
    >
      <ModalContentWrapper>
        <ContentSection>
          <Title palette={palette}>Privacy Notice</Title>
          <Description palette={palette}>
            <p>
              WhatsYourPrice uses cookies and similar technologies to provide essential functionality and to improve
              your experience.
            </p>
            <p>
              Selecting <strong>Allow all</strong> enables all categories of cookies. Selecting{' '}
              <strong>Deny all</strong> will restrict cookies to those required for core site functionality.
            </p>
            <p className="link-line">
              {isMobile ? (
                <>
                  Learn how WhatsYourPrice uses the data you provide:{' '}
                  <PrivacyLink
                    palette={palette}
                    href="https://www.whatsyourprice.com/privacy"
                    target="_blank"
                    rel="noopener"
                  >
                    Privacy Policy
                  </PrivacyLink>
                </>
              ) : (
                <PrivacyLink
                  palette={palette}
                  href="https://www.whatsyourprice.com/privacy"
                  target="_blank"
                  rel="noopener"
                >
                  Learn more in our Privacy Policy
                </PrivacyLink>
              )}
            </p>
          </Description>
        </ContentSection>
        <ButtonsContainer>
          <SecondaryButton
            label="Deny all"
            color="blue"
            size="small"
            customStyle={{
              border: `1px solid ${palette.border}`,
              borderRadius: '4px',
              minWidth: '160px',
              width: '100%',
            }}
            onClick={handleDenyAll}
          />
          <PrimaryButtonV2
            label="Allow all"
            color="blue"
            size="small"
            customStyle={{
              minWidth: '160px',
              width: '100%',
            }}
            onClick={handleAllowAll}
          />
        </ButtonsContainer>
      </ModalContentWrapper>
    </ReactModal>
  );
};

export default CookieConsentModal;
